import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import { toast } from "sonner";
import "@/index.css";
import App from "@/App";
import { getAccessToken, clearTokens } from "@/lib/tokenStore";
import { identityRefresh, redirectToLogin } from "@/lib/identityApi";

// ── Shared state tussen fetch + axios interceptors ──────────────────────────
// Eén refresh tegelijk (N parallelle 401's triggeren niet N refresh-requests),
// één redirect-op-fatal-fail (idempotent na `clearTokens()`).
let _redirectingAfter401 = false;
let _refreshPromise = null;

const doRefreshOnce = () => {
  if (!_refreshPromise) {
    _refreshPromise = identityRefresh().finally(() => {
      _refreshPromise = null;
    });
  }
  return _refreshPromise;
};

const handleFinal401 = () => {
  if (_redirectingAfter401) return;
  const p = window.location.pathname;
  if (p.startsWith("/login") || p.startsWith("/auth/callback")) return;
  _redirectingAfter401 = true;
  clearTokens();
  toast.error("Sessie verlopen, log opnieuw in", { duration: 8000 });
  setTimeout(() => redirectToLogin(), 800);
};

// ── iOS Safari / WebKit HTTP cache workaround + fetch 401 auto-refresh ──────
// WebKit (iOS Safari + Chrome-on-iOS) cached GET responses agressief zelfs
// voor endpoints met Authorization header. Fix: forceer `cache: 'no-store'`.
//
// v18.1.7 — Tevens: auto-refresh JWT bij 401 (spiegelt de axios interceptor).
// Zonder deze wrapper faalde elke lange-duur-submit (bijv. ChecklistFill na
// 2u invullen) silent: token verlopen → 401 → geen retry → user ziet een
// bovenaan scherm verborgen toast en denkt dat de knop niets doet.
const _origFetch = window.fetch.bind(window);
window.fetch = async (input, init = {}) => {
  const url = typeof input === "string" ? input : (input && input.url) || "";
  const isApi = url.includes("/api/");
  const isUploads = url.includes("/api/uploads/");
  const isIdentityAuth = url.includes("/api/auth/refresh") || url.includes("/api/validate");

  if (isApi && !isUploads && init.cache === undefined) {
    init = { ...init, cache: "no-store" };
  }

  let res = await _origFetch(input, init);

  // 401 retry-once voor /api/* (niet voor Identity's eigen auth-endpoints)
  if (
    res.status === 401 &&
    isApi &&
    !isUploads &&
    !isIdentityAuth &&
    !init._tokenRetried
  ) {
    const refreshed = await doRefreshOnce();
    if (refreshed) {
      const newToken = getAccessToken();
      const retryInit = { ...init, _tokenRetried: true };
      if (newToken) {
        if (init.headers instanceof Headers) {
          const h = new Headers(init.headers);
          h.set("Authorization", `Bearer ${newToken}`);
          retryInit.headers = h;
        } else {
          retryInit.headers = {
            ...(init.headers || {}),
            Authorization: `Bearer ${newToken}`,
          };
        }
      }
      res = await _origFetch(input, retryInit);
    } else if (res.status === 401) {
      // Refresh mislukt → sessie echt dood. Toast + redirect.
      handleFinal401();
    }
  }

  return res;
};

// Global axios interceptor: attach in-memory access token to every request.
// Tokens live in tokenStore (singleton), set by AuthCallback after Identity
// redirect. localStorage is no longer used per Identity Platform spec.
axios.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Defense-in-depth voor WebKit caching: stuur expliciet no-cache request
  // headers mee. Browsers respecteren dit en skippen de HTTP cache.
  const url = config.url || "";
  const isApi = url.includes("/api/") || url.startsWith("/api/");
  const isUploads = url.includes("/api/uploads/");
  if (isApi && !isUploads) {
    config.headers = config.headers || {};
    if (!config.headers["Cache-Control"]) {
      config.headers["Cache-Control"] = "no-cache";
    }
  }
  return config;
});

// Global 401 handler for axios: shares refresh-once state with fetch wrapper.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config || {};

    if (status === 401 && !original._retried) {
      original._retried = true;
      const refreshed = await doRefreshOnce();
      if (refreshed) {
        return axios(original);
      }
      handleFinal401();
    }
    return Promise.reject(error);
  },
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
